import type { FlowboxFunction } from './flowbox-types';
declare global {
  interface Window {
    LiSA: any;
    UC_UI_SUPPRESS_CMP_DISPLAY: boolean;
    flowbox: FlowboxFunction;
    iconList: object;
  }
}

export interface CssClassMap {
  [className: string]: boolean;
}

export interface ResponsiveImages {
  bp320: string;
  bp480: string;
  bp768: string;
  bp992: string;
  bp1200: string;
  jpg992Default: string;
  paramImageStyles?: { [key: string]: string };
}

export interface ResponsiveVideos {
  mobileMp4: string;
  tabletMp4?: string;
  desktopMp4: string;
  mobileWebm?: string;
  tabletWebm?: string;
  desktopWebm?: string;
}

export interface Product {
  name: string;
  indexName?: string;
  ctaLabel?: string;
  id: string;
  detailsUrl: string;
  price: string;
  priceUnit: string;
  priceCurrency: string;
  pricePrefix: string;
  bracketPrice: string;
  bracketPriceUnit: string;
  image: string;
  ratingValue: number;
  ratingCount: number;
  noRatingText: string;
  energyEfficiency: EnergyEfficiency[];
  energyEfficiencyClass: string;
  energyEfficiencyRank: string;
  energyEfficiencyLabelUrl: string;
  datasheetUrl: string;
  datasheetLabel: string;
  energyEfficiencyVersion: string;
  ctaText: string;
}

export interface EnergyEfficiency {
  efficiencyClass: string;
  dataSheet: string;
  label: string;
  rank: number;
  labelVersion: EnergyEfficiencyVersion;
  type: string;
  name: string;
  scale: EnergyEfficiencyScale;
}
export interface EnergyEfficiencyScale {
  highest: string;
  lowest: string;
}

export enum EnergyEfficiencyVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export interface Slider {
  images: ResponsiveImages;
  sliderText: string;
  altText: string;
}

export interface Tags {
  tagName: string;
  targetUrl: string;
  seoItem: boolean;
}

export interface ShareOption {
  class: string;
  url: string;
  type: 'facebook' | 'pinterest' | 'content';
  label: string;
  title: string;
}

export interface Video {
  class: string;
  poster: string;
  src: string;
}
export interface YouTubeVideo {
  class: string;
  src: string;
  preview: boolean;
  title: string;
}

export interface VideoPlayList {
  images: ResponsiveImages;
  videoSrc: string;
  thumb: string;
  altText: string;
}
export interface StaticTeasers {
  type: string;
  images?: ResponsiveImages;
  imageSizes?: string;
  youTubeId?: string;
  badge: string;
  target: string;
  targetPageName: string;
  altText: string;
  background?: string;
  imageClassName?: string;
  targetFrame?: string;
}

export interface StaticTeaser {
  staticTeasers: StaticTeasers[];
}

export interface TeaserList {
  teaser: Teaser;
}

export interface Teaser {
  badge?: string;
  altText?: string;
  youtubeId?: string;
  mediaType?: string;
  frameTarget?: string;
  youtubeVideos?: any[];
  videos?: any[];
  keywords?: string[];
  imageDerivates: ImageDerivates;
  rubrics?: string[];
  detail: string;
  headline: string;
  label: string;
  url: string;
  uid: string;
  youtubeThumbnail?: string;
  previewErrors?: PreviewErrors;
}

export interface PreviewErrors {
  noKeyvisual?: string | null;
  anotherRegion?: string | null;
  invalid_container?: string | null;
  isOffline?: string | null;
  noPageSelected?: string | null;
  missingContent?: string | null;
  emptyElement?: string | null;
}
export interface RelKeyword {
  name: string;
}

export interface ImageDerivates {
  images: Image[];
  altText: string;
}
export interface RelRubric {
  name: string;
}

export interface Image {
  size: string;
  width: number;
  url: string;
  height: number;
}

export interface MarketAddress {
  city: string;
  street: string;
  zipCode: string;
  phone: string;
  phoneNumberFormatted: string;
  mapUrl: string;
  mapUrlLabel: string;
  contactUrl: string;
  contactLabel: string;
}

export interface MarketOpenTimesInfo {
  openTimesHeadline: string;
  timesLabel: string;
  holidaysLabel: string;
  openTimes: MarketOpenTimes[];
  openSpecialTimes: MarketSpecialOpenTimes[];
}

export interface MarketOpenTimes {
  day: string;
  time: string;
}

export interface MarketSpecialOpenTimes {
  timesLabel: string;
  open: boolean;
  closedLabel: string;
  specialDateLabel: string;
  timeFrom: string;
  timeTo: string;
}

export interface searchStore {
  headline: string;
  buttonLabel: string;
  searchPageUrl: string;
  nearStoresLabel: string;
  nearStores: nearStores[];
}

export interface nearStores {
  storeUrl: string;
  storeCity: string;
  storeDistance: string;
}

export interface CmsInpageNavigationConfig {
  sticky: boolean;
  items: CmsInpageNavigationItem[];
}

export interface CmsInpageNavigationItem {
  scrollId: string;
  target: '_self' | '_blank' | '_top';
  text: string;
}

export interface PagesList {
  name: string;
  pageUrl: string;
}

export interface StoresList {
  name: string;
  storePageUrl: string;
}
export interface Stores {
  navigationUrl: string;
  labels: StoreLabels;
  stores: Store[];
}

export interface StoreLabels {
  closed: string;
  time: string;
}

export interface Store {
  city: string;
  id: string;
  name: string;
  phone: string;
  phoneInternational: string;
  postalcode: string;
  street: string;
  url: string;
  regularTimes: OpenTimes[];
  specialTimes: SpecialTimes[];
  images: ResponsiveImages;
}

export interface StoreMap {
  city: string;
  id?: string;
  name: string;
  postalcode: string;
  street: string;
  url: string;
  longitude: number;
  latitude: number;
  marker?: string;
  client?: string;
}

export interface MarkerOptions {
  position: {
    lat: number;
    lng: number;
  };
  icon: string;
  name: string;
  id: string;
  street: string;
  postalcode: string;
  city: string;
  url: string;
  client: string;
}

export interface OpenTimes {
  day: string;
  from: string;
  until: string;
}

export interface SpecialTimes {
  isOpen: boolean;
  from: string;
  until: string;
  date: string;
}

export interface FetchTeaserServiceConfig {
  locale: string;
  companyCode: string;
  responsePage?: number;
  responseTeaserCount?: number;
  rubric?: string;
  tag?: string;
  mediaType?: string;
  template?: string;
}

export interface CmsCategorySliderConfig {
  headline: string;
  minSlides: number;
  fallback: string;
  background: boolean;
  items: CmsCategorySliderItem[];
}

export interface CmsCategorySliderItem {
  name: string;
  titleText: string;
  url: string;
  image: CmsCategorySliderItemImage;
}

export interface AssortmentTeaser {
  name: string;
  titleText: string;
  url: string;
  image: CmsCategorySliderItemImage;
}

export interface CategoryTeaser {
  alt: string;
  image: string;
  indexName: string;
  label: string;
  detailsUrl: string;
  badge?: string;
}

interface CmsCategorySliderItemImage {
  url: string;
  alt: string;
  width: number;
  height: number;
}

export interface Contact {
  description: string;
  picture: ResponsiveImages | string;
  phone: string;
  mobile: string;
  mail: string;
}

export interface ContactLabels {
  whatsapp: string;
}

export interface TableContent {
  rows: Array<Array<TableCell>>;
}

export interface TableCell {
  colspan: number;
  rowspan: number;
  content: string;
  valign: string;
  halign: string;
  column: number;
}
export interface HotSpot {
  top: string;
  left: string;
  ranking: string;
  headline: string;
  text: string;
}

export interface MediaTypeController {
  altText: string;
  images: ResponsiveImages;
  imageSizes: string;
  className: string;
  youTubeId: string;
  mediaType: MediaType;
}

export interface MediaType {
  image: object;
  ytvideo: object;
  video: object;
}

export interface ImageParams {
  responsiveImages: ResponsiveImages;
  imageSizes: string;
  altText: string;
  imageClass: string;
  imageStyles: { [key: string]: string };
  lazyLoad: boolean;
}

export interface ImageParams {
  responsiveImages: ResponsiveImages;
  imageSizes: string;
  altText: string;
  imageClass: string;
  imageStyles: { [key: string]: string };
  lazyLoad: boolean;
}

export interface ContentServiceResult {
  caas: ContentServiceResponse;
}

export interface ContentServiceResponse {
  total: number;
  facets: ContentServiceFacet[];
  documents: ContentServiceDocument[];
}

export interface ContentServiceFacet {
  id: string;
  count: number;
  resultCount: number;
  values: ContentServiceFacetValue[];
}

export interface ContentServiceFacetValue {
  value: string;
  count: number;
  filter: string;
}

export interface ContentServiceDocument {
  document: ContentServiceDocumentValueWrapper;
}

export interface ContentServiceDocumentValueWrapper {
  data: ContentServiceDocumentValue;
}

export interface ContentServiceDocumentValue {
  title: string;
  label: string;
  description: string;
  timestamp: number;
  keyvisual_image: ContentServiceKeyVisualImageWrapper;
  rubrics: string[];
  tags: string[];
  pageTemplate: string;
  url: string;
  id: string;
  mediaType: string;
  badge: string;
  videos: Video[];
}

export interface ContentServiceKeyVisualImageWrapper {
  image: ContentServiceKeyVisualImage;
}

export interface ContentServiceKeyVisualImage {
  hero_teaser_320x213: string;
  hero_teaser_996x664: string;
  hero_teaser_1600x1067: string;
  hero_teaser_1200x800: string;
  hero_teaser_768x512: string;
  hero_teaser_480x320: string;
  ORIGINAL: string;
}

export interface ContentServiceRubric {
  name: string;
  count: number;
  tags: ContentServiceTag[];
}

export interface ContentServiceTag {
  name: string;
  count: number;
}

export interface ContentServiceVideo {
  url: string;
  type: string;
  width: number;
}

export interface Rubric {
  name: string;
  tags: string[];
}

export interface Icon {
  path: string;
  viewBox?: string;
  class?: string;
}

export interface SplideComponentConfig {
  arrowLeftLabel: string;
  arrowRightLabel: string;
}
